"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_OWN_PAGE_LOAD_ISSUE = exports.normalizeAppArtifactContentType = exports.APP_ARTIFACT_CONTENT_TYPES = exports.APP_BASE_BINARY_CONTENT_TYPES = void 0;
exports.appFileByteLimit = appFileByteLimit;
exports.appArtifactTextBody = appArtifactTextBody;
exports.isAppBaseJavascriptContentType = isAppBaseJavascriptContentType;
exports.appArtifactPathIssue = appArtifactPathIssue;
exports.appArtifactStaticPolicyIssue = appArtifactStaticPolicyIssue;
exports.appActionMarkerIssues = appActionMarkerIssues;
exports.appSourceFileIssues = appSourceFileIssues;
exports.appRoutePathKey = appRoutePathKey;
exports.appArtifactRoutePathIssue = appArtifactRoutePathIssue;
exports.appArtifactIssues = appArtifactIssues;
/**
 * Static hosting policy for AppBase artifact files.
 *
 * One rule set for every place that decides whether a file may ship: the
 * server artifact validator (upload, apply, marketplace install) and the local
 * StreamKit check. Rules read file text only; nothing is executed.
 */
const app_base_config_1 = require("./app-base-config");
const app_base_contract_1 = require("./app-base-contract");
Object.defineProperty(exports, "normalizeAppArtifactContentType", { enumerable: true, get: function () { return app_base_contract_1.normalizeAppArtifactContentType; } });
const app_base_data_query_validation_1 = require("./app-base-data-query-validation");
const app_base_html_document_1 = require("./app-base-html-document");
const app_base_template_markers_1 = require("./app-base-template-markers");
const app_base_url_policy_1 = require("./app-base-url-policy");
/** Binary assets remain inert and cannot bypass text hosting policy. */
exports.APP_BASE_BINARY_CONTENT_TYPES = new Set([
    'font/woff2',
    'font/woff',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'image/x-icon',
]);
/** The bytes one App file of `contentType` may hold: a binary or a text file. */
function appFileByteLimit(contentType) {
    return exports.APP_BASE_BINARY_CONTENT_TYPES.has((0, app_base_contract_1.normalizeAppArtifactContentType)(contentType))
        ? app_base_contract_1.APP_BASE_BUDGETS.maxBinaryFileBytes
        : app_base_contract_1.APP_BASE_BUDGETS.maxFileBytes;
}
/**
 * The bytes of an inline text file; storage and delivery hash the exact same
 * bytes. A binary file has no inline body: it is a stored file that names
 * its blob.
 */
function appArtifactTextBody(file) {
    if (exports.APP_BASE_BINARY_CONTENT_TYPES.has((0, app_base_contract_1.normalizeAppArtifactContentType)(file.contentType)))
        throw new Error('Binary artifact assets require a stored file');
    const bytes = new TextEncoder().encode(file.text);
    if (bytes.length > app_base_contract_1.APP_BASE_BUDGETS.maxFileBytes)
        throw new Error('Artifact body exceeds file budget');
    return bytes;
}
exports.APP_ARTIFACT_CONTENT_TYPES = new Set([
    ...exports.APP_BASE_BINARY_CONTENT_TYPES,
    'text/html',
    'text/css',
    'text/javascript',
    'application/javascript',
    'application/json',
    'image/svg+xml',
    'text/plain',
]);
const FORBIDDEN_EXTENSIONS = [
    '.cjs',
    '.php',
    '.py',
    '.rb',
    '.sh',
    '.wasm',
];
const FORBIDDEN_TEXT_PATTERNS = [
    {
        pattern: /<script\b[^>]*\bsrc\s*=\s*["'](?:https?:)?\/\//i,
        message: 'Remote script URLs are not allowed',
    },
    {
        pattern: /\bimport\s*\(\s*["'](?:https?:)?\/\//i,
        message: 'Remote dynamic imports are not allowed',
    },
    {
        pattern: /\bfrom\s*["'](?:https?:)?\/\//i,
        message: 'Remote module imports are not allowed',
    },
    {
        // Case matters: eval and the constructor Function, never a function( of
        // an ordinary function expression or declaration.
        pattern: /\b(?:eval|Function)\s*\(/,
        message: 'eval and Function constructors are not allowed',
    },
    {
        pattern: /\b(?:fetch|sendBeacon|axios)\s*\(|\bXMLHttpRequest\b/i,
        message: 'Direct network calls are not allowed; use querySource and platform capabilities',
    },
    {
        pattern: /\b(?:fetchDataset|fetchDatasets|getDataset|readWindow|subscribeDataset|subscribeDatasets)\s*\(/i,
        message: 'Legacy data aliases are not allowed; use querySource and watchSource',
    },
    {
        pattern: /\b(?:WebSocket|EventSource)\s*\(\s*["'`](?:(?:https?|wss?):)?\/\//i,
        message: 'External live connections are not allowed',
    },
    {
        pattern: /\bset(?:Timeout|Interval)\s*\(\s*["'`]/i,
        message: 'String-based timers are not allowed',
    },
    {
        // Case matters: the constructors and navigator.serviceWorker, never the
        // word worker in a text, a label or a class name ("Co-worker", .worker).
        pattern: /\b(?:Worker|SharedWorker|ServiceWorker|serviceWorker)\b/,
        message: 'Workers and service workers are not allowed',
    },
    {
        pattern: /\bWebAssembly\b|\.wasm\b/i,
        message: 'WebAssembly is not allowed',
    },
    {
        pattern: /["'`]\/(?:graphql\b|api\b|_data\b)|["'`][^"'`]*__streamkit\b/i,
        message: 'Workspace GraphQL, platform data and StreamKit transport endpoints are not allowed',
    },
    {
        pattern: /<(?:form\b[^>]*\saction|(?:button|input)\b[^>]*\sformaction)\s*=/i,
        message: 'Form posts are not allowed in App artifacts',
    },
    {
        pattern: /<[^>]+\b(?:src|poster|srcset|imagesrcset)\s*=\s*["'](?:https?:)?\/\//i,
        message: 'Remote HTML asset URLs are not allowed',
    },
    {
        // The `href` of a link is a target, not an asset; the URL policy checks it.
        pattern: /<(?!(?:a|area)\b)[^>]+\bhref\s*=\s*["'](?:https?:)?\/\//i,
        message: 'Remote HTML asset URLs are not allowed',
    },
    {
        pattern: /<iframe\b/i,
        message: 'Frames are not allowed in App artifacts',
    },
    {
        pattern: /@import\s+(?:url\()?["']?(?:https?:)?\/\//i,
        message: 'Remote CSS imports are not allowed',
    },
    {
        pattern: /url\(\s*["']?(?:https?:)?\/\//i,
        message: 'Remote CSS asset URLs are not allowed',
    },
];
const HTML_URL_ATTRIBUTES = new Set([
    'action',
    'formaction',
    'href',
    'imagesrcset',
    'poster',
    'src',
    'srcset',
    'xlink:href',
]);
const INTERACTIVE_HTML_TAGS = new Set(['button', 'select', 'textarea']);
const INTERACTIVE_HTML_ROLES = new Set([
    'button',
    'checkbox',
    'combobox',
    'link',
    'listbox',
    'menuitem',
    'option',
    'radio',
    'searchbox',
    'slider',
    'spinbutton',
    'switch',
    'tab',
    'textbox',
]);
const SEMANTIC_INTERACTION_ATTRIBUTES = [
    'data-sl-action',
    'data-sl-control',
    'data-sl-field',
    'data-sl-route',
];
/** True for artifact files that run as JavaScript modules. */
function isAppBaseJavascriptContentType(contentType) {
    const normalized = (0, app_base_contract_1.normalizeAppArtifactContentType)(contentType);
    return (normalized === 'text/javascript' || normalized === 'application/javascript');
}
/** Relative, traversal-free path with an allowed extension; null when valid. */
function appArtifactPathIssue(path) {
    if (path.startsWith('/') || path.includes('\\')) {
        return `Invalid App file path ${path}`;
    }
    const parts = path.split('/').filter(Boolean);
    if (parts.length === 0 || parts.some(part => part === '..' || part === '.')) {
        return `Invalid App file path ${path}`;
    }
    const lower = path.toLowerCase();
    const extension = FORBIDDEN_EXTENSIONS.find(ext => lower.endsWith(ext));
    return extension ? `App file extension ${extension} is not allowed` : null;
}
/** A file that leaves the page on its own; the App changes pages through the platform. */
exports.APP_OWN_PAGE_LOAD_ISSUE = 'reloads the page on its own; change pages with a link or StreamLainAppBase.navigate(path)';
const BROWSER_LOCATION = String.raw `(?:(?<![\w$.])(?<!\b(?:const|let|var)\s+)|\b(?:window|document|self|globalThis|top|parent)\s*\.\s*)location`;
/** `location.hash` and `history.replaceState` keep the page and stay allowed. */
const OWN_PAGE_LOAD_SCRIPT_PATTERN = new RegExp([
    String.raw `${BROWSER_LOCATION}\s*\.\s*(?:assign|replace|reload)\s*\(`,
    String.raw `${BROWSER_LOCATION}(?:\s*\.\s*(?:href|pathname|search))?\s*=(?![=>])`,
    String.raw `\bhistory\s*\.\s*pushState\s*\(`,
].join('|'));
const OWN_PAGE_LOAD_MARKUP_PATTERN = /<meta\b[^>]*\bhttp-equiv\s*=\s*["']?refresh\b/i;
/**
 * First static hosting violation of one text file, or null. `contentType`
 * must already be normalized.
 */
function appArtifactStaticPolicyIssue(path, contentType, text) {
    if (contentType !== 'text/html' &&
        contentType !== 'text/css' &&
        contentType !== 'text/javascript' &&
        contentType !== 'application/javascript' &&
        contentType !== 'image/svg+xml') {
        return null;
    }
    const rule = FORBIDDEN_TEXT_PATTERNS.find(item => item.pattern.test(text));
    if (rule) {
        return `App file ${path} violates static hosting policy: ${rule.message}`;
    }
    const ownPageLoad = isAppBaseJavascriptContentType(contentType)
        ? OWN_PAGE_LOAD_SCRIPT_PATTERN
        : contentType === 'text/html'
            ? OWN_PAGE_LOAD_MARKUP_PATTERN
            : null;
    if (ownPageLoad?.test(text)) {
        return `App file ${path} ${exports.APP_OWN_PAGE_LOAD_ISSUE}`;
    }
    if (isAppBaseJavascriptContentType(contentType)) {
        const queryIssue = (0, app_base_data_query_validation_1.listAppBaseDataQueryIssues)(text)[0];
        if (queryIssue) {
            return `App file ${path} violates data query policy: ${queryIssue.message}`;
        }
    }
    const urlIssue = structuredUrlIssue(path, contentType, text);
    if (urlIssue)
        return urlIssue;
    if (contentType === 'text/html') {
        const markerIssue = interactiveControlMarkerIssue(path, text);
        if (markerIssue)
            return markerIssue;
        if (/<script\b(?![^>]*\bsrc\s*=)[^>]*>/i.test(text)) {
            return `App file ${path} violates static hosting policy: inline scripts are not allowed`;
        }
    }
    return null;
}
/**
 * Every action of an App whose control the files do not mark, and every form
 * field of a row insert without a marker. `text` is the text of all text
 * files of the App together. The issues come action by action, an action
 * before its fields.
 */
function appActionMarkerIssues(actions, text) {
    const issues = [];
    const markers = (0, app_base_template_markers_1.appBaseMarkerValues)(text, [
        'data-sl-action',
        'data-sl-control',
        'data-sl-field',
    ]);
    const marked = (art, value) => markers.get(art)?.has(value) ?? false;
    for (const action of actions) {
        if ((0, app_base_config_1.appActionHostOnly)(action))
            continue;
        if (!marked('data-sl-action', action.id) &&
            !marked('data-sl-control', `action:${action.id}`)) {
            issues.push(`App action ${action.id} requires a semantic UI marker data-sl-action="${action.id}" or data-sl-control="action:${action.id}"`);
        }
        if (action.type !== 'rowInsert')
            continue;
        for (const field of action.fields) {
            if (!marked('data-sl-field', field.columnId) &&
                !marked('data-sl-control', `action:${action.id}:field:${field.columnId}`)) {
                issues.push(`App action field ${field.columnId} requires a semantic UI marker data-sl-field="${field.columnId}" or data-sl-control="action:${action.id}:field:${field.columnId}"`);
            }
        }
    }
    return issues;
}
/**
 * Every issue of a set of source files that together form one App:
 * path, content type, body encoding, per-file size, static hosting policy of
 * text files and the byte limit of the App Container. Used by the local App Dev
 * Kit check before upload and by the App package reader and writer.
 */
function appSourceFileIssues(files) {
    const issues = [];
    let totalBytes = 0;
    for (const file of files) {
        const pathIssue = appArtifactPathIssue(file.path);
        if (pathIssue)
            issues.push(pathIssue);
        const contentType = (0, app_base_contract_1.normalizeAppArtifactContentType)(file.contentType);
        if (!exports.APP_ARTIFACT_CONTENT_TYPES.has(contentType)) {
            issues.push(`Content type ${file.contentType} is not allowed for App artifacts`);
            continue;
        }
        if (file.storageKey !== undefined &&
            file.text === undefined &&
            exports.APP_BASE_BINARY_CONTENT_TYPES.has(contentType)) {
            // Its bytes were checked against its type when it was stored.
            if (file.bytes === undefined)
                issues.push(`App file ${file.path} has no byte count`);
            else if (file.bytes > app_base_contract_1.APP_BASE_BUDGETS.maxBinaryFileBytes)
                issues.push(`App file ${file.path} exceeds ${app_base_contract_1.APP_BASE_BUDGETS.maxBinaryFileBytes} bytes`);
            else
                totalBytes += file.bytes;
            continue;
        }
        if (file.text === undefined) {
            issues.push(`App file ${file.path} has neither text nor a stored blob`);
            continue;
        }
        let body;
        try {
            body = appArtifactTextBody({ contentType, text: file.text });
        }
        catch (error) {
            issues.push(`App file ${file.path}: ${error instanceof Error ? error.message : 'Invalid artifact body'}`);
            continue;
        }
        totalBytes += body.byteLength;
        const policyIssue = appArtifactStaticPolicyIssue(file.path, contentType, file.text);
        if (policyIssue)
            issues.push(policyIssue);
    }
    if (totalBytes > app_base_contract_1.APP_BASE_BUDGETS.maxTotalBytes) {
        issues.push(`${totalBytes} bytes (max ${app_base_contract_1.APP_BASE_BUDGETS.maxTotalBytes} for the App) in one App Module`);
    }
    return issues;
}
function interactiveControlMarkerIssue(path, text) {
    for (const { tagName, attributes } of (0, app_base_html_document_1.appHtmlElements)(text)) {
        if (!isInteractiveHtmlElement(tagName, attributes))
            continue;
        if (SEMANTIC_INTERACTION_ATTRIBUTES.some(attribute => attributes.has(attribute))) {
            continue;
        }
        return `App file ${path} interactive <${tagName}> control requires data-sl-control, data-sl-action, data-sl-field, or data-sl-route`;
    }
    return null;
}
function isInteractiveHtmlElement(tagName, attributes) {
    if (INTERACTIVE_HTML_TAGS.has(tagName))
        return true;
    if (tagName === 'input') {
        return attributes.get('type')?.toLowerCase() !== 'hidden';
    }
    if (tagName === 'a' && attributes.has('href'))
        return true;
    const role = attributes.get('role')?.toLowerCase();
    return (attributes.has('onclick') ||
        (role !== undefined && INTERACTIVE_HTML_ROLES.has(role)));
}
function structuredUrlIssue(path, contentType, text) {
    const urls = [];
    if (contentType === 'text/html' || contentType === 'image/svg+xml') {
        urls.push(
        // Only a page has links; an image never leads outside the App.
        ...extractHtmlUrls(text, contentType === 'text/html' ? htmlLinkTargetOffsets(text) : new Set()), ...extractCssUrls(text));
    }
    if (contentType === 'text/css')
        urls.push(...extractCssUrls(text));
    if (isAppBaseJavascriptContentType(contentType)) {
        urls.push(...extractJavaScriptNetworkSinkUrls(text));
    }
    for (const item of urls) {
        const issue = artifactUrlIssue(path, item.url, item.surface, item.target);
        if (issue)
            return issue;
    }
    return null;
}
const HTML_TAG = /<([a-zA-Z][a-zA-Z0-9:-]*)(?:\s+[^\s"'<>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'<>=`]+))?)*\s*\/?>/g;
const HTML_LINK_TAGS = new Set(['a', 'area']);
/** Offsets of the `href` attributes of complete `<a>` and `<area>` tags. */
function htmlLinkTargetOffsets(text) {
    const offsets = new Set();
    for (const tag of text.matchAll(HTML_TAG)) {
        if (!HTML_LINK_TAGS.has(tag[1].toLowerCase()))
            continue;
        // A tag left open before this one takes it in as its own attributes.
        const closed = text.lastIndexOf('>', tag.index);
        if (/<[a-zA-Z/!?]/.test(text.slice(closed + 1, tag.index)))
            continue;
        for (const attribute of tag[0].matchAll(/\bhref\s*=\s*["']/gi)) {
            offsets.add(tag.index + attribute.index);
        }
    }
    return offsets;
}
function extractHtmlUrls(text, linkTargetOffsets) {
    const urls = [];
    const quoted = /\b([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(["'])([\s\S]*?)\2/g;
    for (const match of text.matchAll(quoted)) {
        const attribute = match[1].toLowerCase();
        if (!HTML_URL_ATTRIBUTES.has(attribute))
            continue;
        const rawValue = match[3].trim();
        const surface = `html:${attribute}`;
        if (attribute === 'srcset' || attribute === 'imagesrcset') {
            urls.push(...extractSrcSetUrls(rawValue).map(url => ({ url, surface })));
            continue;
        }
        urls.push({
            url: rawValue,
            surface,
            target: linkTargetOffsets.has(match.index) ? 'link' : 'resource',
        });
    }
    return urls;
}
function extractSrcSetUrls(value) {
    return value
        .split(',')
        .map(candidate => candidate.trim().split(/\s+/)[0]?.trim())
        .filter((url) => Boolean(url));
}
function extractCssUrls(text) {
    const urls = [];
    const urlFunction = /url\(\s*(["']?)([\s\S]*?)\1\s*\)/gi;
    for (const match of text.matchAll(urlFunction)) {
        urls.push({ url: match[2].trim(), surface: 'css:url' });
    }
    const imports = /@import\s+(?:url\(\s*)?(["']?)([^"')\s;]+)\1(?:\s*\))?/gi;
    for (const match of text.matchAll(imports)) {
        urls.push({ url: match[2].trim(), surface: 'css:@import' });
    }
    return urls;
}
function extractJavaScriptNetworkSinkUrls(text) {
    const urls = [];
    const networkSink = /\b(fetch|sendBeacon|WebSocket|EventSource)\s*\(\s*(["'`])([^"'`]+)\2/gi;
    for (const match of text.matchAll(networkSink)) {
        urls.push({ url: match[3].trim(), surface: `js:${match[1]}` });
    }
    return urls;
}
function artifactUrlIssue(path, url, surface, target) {
    const value = url.trim();
    if (!(0, app_base_url_policy_1.appBaseUrlPolicyIssue)(value, target))
        return null;
    if (/^data:/i.test(value)) {
        const mediaType = value
            .slice(5)
            .split(',', 1)[0]
            .split(';', 1)[0]
            .toLowerCase();
        return `App file ${path} violates static hosting policy: ${surface} data URL media type "${mediaType}" is not allowed`;
    }
    return `App file ${path} violates static hosting policy: ${surface} URL "${value}" is not allowed`;
}
/** Platform paths a cache policy may name, by the cache class they belong to. */
const PLATFORM_ROUTE_POLICY_PREFIXES = {
    customData: ['/_data'],
    customEvents: ['/_app-events'],
    customAction: ['/_app-action'],
    internalLiveData: ['/_data'],
    internalLiveEvents: ['/_app-live/events'],
    internalLiveCommand: ['/_app-live/command'],
    automationStatus: ['/_app-events'],
    portal: ['/_portal'],
};
/** A route path without trailing slashes: the form two paths are compared in. */
function appRoutePathKey(path) {
    return path.replace(/\/+$/g, '') || '/';
}
/**
 * The route path rule of a stored artifact: absolute, inside the App, no
 * backslash and no URL; null when the path keeps it. `appRoutePathIssue` of
 * the contract is a different rule: it answers an author and counts segments.
 */
function appArtifactRoutePathIssue(path) {
    if (!path.startsWith('/') ||
        path.includes('\\') ||
        path.includes('://') ||
        path.startsWith('//')) {
        return `Invalid App route path ${path}`;
    }
    const parts = path.split('/').filter(Boolean);
    return parts.some(part => part === '..' || part === '.')
        ? `Invalid App route path ${path}`
        : null;
}
function isPlatformRouteCachePolicy(path, cacheClass) {
    const key = appRoutePathKey(path);
    return (PLATFORM_ROUTE_POLICY_PREFIXES[cacheClass]?.some(prefix => key === prefix || key.startsWith(`${prefix}/`)) ?? false);
}
/**
 * What an artifact breaks of the rules that the artifact alone decides, in the
 * order the server checks them: menu size, manifest size, cache policies on
 * routes the App has, confirmed public live data, fields of a row insert and
 * HTML entry files. Three cases belong to other rules and are left to them: a
 * cache policy with an unsafe path, live data on a static delivery, and an
 * entry that is missing or has an unsafe path.
 */
function appArtifactIssues(artifact) {
    const { manifest } = artifact;
    const issues = [];
    if (manifest.navigation.length > app_base_contract_1.APP_BASE_BUDGETS.maxNavigationItems) {
        issues.push(`App manifest exceeds ${app_base_contract_1.APP_BASE_BUDGETS.maxNavigationItems} navigation items`);
    }
    if (new TextEncoder().encode(JSON.stringify(manifest)).length >
        app_base_contract_1.APP_BASE_BUDGETS.maxManifestBytes) {
        issues.push(`App manifest exceeds ${app_base_contract_1.APP_BASE_BUDGETS.maxManifestBytes} bytes`);
    }
    const routeKeys = new Set(manifest.routes.map(route => appRoutePathKey(route.path)));
    for (const policy of manifest.delivery.routePolicies) {
        if (appArtifactRoutePathIssue(policy.path) === null &&
            !routeKeys.has(appRoutePathKey(policy.path)) &&
            !isPlatformRouteCachePolicy(policy.path, policy.cacheClass)) {
            issues.push(`App route cache policy points to unknown route ${policy.path}`);
        }
    }
    for (const action of manifest.actions) {
        if (action.type === 'rowInsert' &&
            action.fields.length > app_base_contract_1.APP_BASE_BUDGETS.maxActionFields) {
            issues.push(`App action ${action.id} exceeds ${app_base_contract_1.APP_BASE_BUDGETS.maxActionFields} fields`);
        }
    }
    for (const entry of [
        manifest.entry,
        ...manifest.routes.map(route => route.entry),
    ]) {
        const file = artifact.files[entry];
        if (appArtifactPathIssue(entry) === null &&
            file &&
            (0, app_base_contract_1.normalizeAppArtifactContentType)(file.contentType) !== 'text/html') {
            issues.push(`App entry ${entry} must be HTML`);
        }
    }
    return [...new Set(issues)];
}
